import React, {useCallback, useEffect, useState} from 'react';
import {AlertTriangleIcon, CheckCheckIcon, EyeIcon, EyeOffIcon, ShieldIcon} from '../components/icons';
import AuthShell from '../components/auth/AuthShell';
import SmartCaptcha, {isCaptchaEnabled} from '../components/auth/SmartCaptcha';
import Spinner from '../components/Spinner';
import {AuthApiError, confirmPasswordReset, passwordResetStatus} from '../service/RegistrationService';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

type PageState = 'checking' | 'form' | 'invalid' | 'done';

/**
 * Смена пароля по ссылке из письма
 * (`docs/features/2026-08-20_auth-registration-recovery`, FR-24/FR-28).
 *
 * Поле кода показывается, только если сервер сказал, что у аккаунта включена 2FA:
 * без кода пароль не сменить, и это не обходится подделкой ответа — проверку делает
 * сервер (FR-15).
 */
const ResetPasswordPage: React.FC = () => {
    useDocumentTitle();
    const [state, setState] = useState<PageState>('checking');
    const [token, setToken] = useState('');
    const [twoFactorRequired, setTwoFactorRequired] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [code, setCode] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const captchaRequired = isCaptchaEnabled();
    const handleCaptchaToken = useCallback((value: string) => setCaptchaToken(value), []);

    useEffect(() => {
        const raw = new URLSearchParams(window.location.search).get('token') || '';
        setToken(raw);
        if (!raw) {
            setState('invalid');
            return;
        }
        let cancelled = false;
        passwordResetStatus(raw)
            .then(status => {
                if (cancelled) {
                    return;
                }
                setTwoFactorRequired(status.twoFactorRequired);
                setState(status.valid ? 'form' : 'invalid');
            })
            .catch(() => {
                if (!cancelled) {
                    setState('invalid');
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmation) {
            setError('Пароли не совпадают.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await confirmPasswordReset(token, password, code, captchaToken);
            setState('done');
        } catch (err) {
            if (err instanceof AuthApiError) {
                if (err.code === 'RESET_TOKEN_INVALID') {
                    // Ссылка сгорела (просрочка или лимит неверных кодов) — форму убираем.
                    setState('invalid');
                    return;
                }
                if (err.code === 'MFA_REQUIRED') {
                    setTwoFactorRequired(true);
                }
                setError(err.description);
                return;
            }
            setError('Не удалось сменить пароль. Попробуйте позже.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            heading={<>Новый пароль<br/><span className="text-brand-600 dark:text-brand-400">для вашего аккаунта</span></>}
            lead="После смены пароля все активные сессии завершатся — на других устройствах нужно будет войти заново."
        >
            <div data-testid="reset-password-page">
                {state === 'checking' && (
                    <div className="mt-6 text-center">
                        <Spinner/>
                        <p className="mt-4 text-sm text-ink-500 dark:text-ink-400">Проверяем ссылку…</p>
                    </div>
                )}

                {state === 'invalid' && (
                    <div className="mt-6 text-center" data-testid="reset-invalid">
                        <span className="mx-auto w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center">
                            <AlertTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400"/>
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-ink-900 dark:text-ink-50">Ссылка не подошла</h2>
                        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">
                            Она устарела, уже использована или была отменена. Запросите смену пароля заново.
                        </p>
                        <a href="/forgot-password" className="btn-accent h-12 mt-6 w-full">Запросить новую ссылку</a>
                    </div>
                )}

                {state === 'done' && (
                    <div className="mt-6 text-center" data-testid="reset-done">
                        <span className="mx-auto w-12 h-12 rounded-full bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center">
                            <CheckCheckIcon className="w-6 h-6 text-brand-600 dark:text-brand-400"/>
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-ink-900 dark:text-ink-50">Пароль изменён</h2>
                        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">
                            Войдите с новым паролем. Остальные сессии завершены.
                        </p>
                        <a href="/login" className="btn-accent h-12 mt-6 w-full">Войти</a>
                    </div>
                )}

                {state === 'form' && (
                    <>
                        <h2 className="mt-6 text-2xl font-bold text-center text-ink-900 dark:text-ink-50">
                            Задайте новый пароль
                        </h2>
                        <p className="mt-2 text-sm text-center text-ink-500 dark:text-ink-400">
                            Минимум 10 символов, хотя бы одна буква.
                        </p>

                        {error && (
                            <div
                                role="alert"
                                className="mt-6 p-3.5 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30"
                            >
                                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                            </div>
                        )}

                        <form onSubmit={onSubmit} className="mt-6 space-y-5">
                            <div>
                                <label htmlFor="new-password"
                                       className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                    Новый пароль
                                </label>
                                <div className="relative">
                                    <input
                                        id="new-password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="input-field h-12 pr-12"
                                        autoComplete="new-password"
                                        minLength={10}
                                        required
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 px-3 flex items-center text-ink-400 hover:text-ink-600 dark:hover:text-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                        aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOffIcon className="w-5 h-5"/> : <EyeIcon className="w-5 h-5"/>}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="confirmation"
                                       className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                    Повторите пароль
                                </label>
                                <input
                                    id="confirmation"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmation}
                                    onChange={(e) => setConfirmation(e.target.value)}
                                    className="input-field h-12"
                                    autoComplete="new-password"
                                    required
                                />
                            </div>

                            {twoFactorRequired && (
                                <div data-testid="reset-2fa-field">
                                    <label htmlFor="code"
                                           className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                        Код двухфакторной аутентификации
                                    </label>
                                    <input
                                        id="code"
                                        type="text"
                                        inputMode="numeric"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        className="input-field h-12 tracking-widest"
                                        placeholder="123456"
                                        autoComplete="one-time-code"
                                        required
                                    />
                                    <p className="mt-2 flex items-start gap-2 text-xs text-ink-500 dark:text-ink-400">
                                        <ShieldIcon className="w-4 h-4 shrink-0 text-brand-500"/>
                                        Код из приложения-аутентификатора или один из recovery-кодов.
                                        Без него пароль не изменится.
                                    </p>
                                </div>
                            )}

                            <SmartCaptcha onToken={handleCaptchaToken}/>

                            <button
                                type="submit"
                                disabled={loading || (captchaRequired && !captchaToken)}
                                className="btn-accent w-full h-12 text-[15px]"
                            >
                                {loading ? 'Сохраняем...' : 'Сменить пароль'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </AuthShell>
    );
};

export default ResetPasswordPage;
